/*!
 * FunASR realtime Web SDK
 * Captures microphone audio, streams 16 kHz PCM16 over WebSocket and reports
 * partial (streaming) and final (corrected, punctuated) transcripts.
 *
 *   const rec = new FunASR.Recognizer({ url: "ws://localhost:10095/ws",
 *     onPartial: (text) => {}, onFinal: (sentence) => {} });
 *   await rec.start();
 *   await rec.stop();
 *
 * Works as a <script> global (window.FunASR), CommonJS or AMD module.
 */
(function (root, factory) {
  if (typeof define === "function" && define.amd) define([], factory);
  else if (typeof module === "object" && module.exports) module.exports = factory();
  else root.FunASR = factory();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var TARGET_RATE = 16000;
  // 60 ms of 16 kHz audio, the frame size the server processes.
  var FRAME_SAMPLES = 960;

  // Runs on the audio thread: downmixes, resamples to 16 kHz and posts Int16 frames.
  var WORKLET_SOURCE = [
    "class PcmCapture extends AudioWorkletProcessor {",
    "  constructor(options) {",
    "    super();",
    "    this.ratio = sampleRate / options.processorOptions.targetRate;",
    "    this.frame = options.processorOptions.frameSamples;",
    "    this.buf = new Int16Array(this.frame);",
    "    this.len = 0;",
    "    this.pos = 0;",
    "    this.prev = 0;",
    "  }",
    "  process(inputs) {",
    "    const input = inputs[0];",
    "    if (!input || input.length === 0) return true;",
    "    const ch0 = input[0];",
    "    const n = ch0.length;",
    "    const mono = new Float32Array(n);",
    "    for (let c = 0; c < input.length; c++) {",
    "      const ch = input[c];",
    "      for (let i = 0; i < n; i++) mono[i] += ch[i] / input.length;",
    "    }",
    // Linear interpolation resampler; `pos` is the fractional read position carried across blocks.
    "    while (this.pos < n) {",
    "      const i = Math.floor(this.pos);",
    "      const frac = this.pos - i;",
    "      const a = i === 0 ? this.prev : mono[i - 1];",
    "      const b = mono[i];",
    "      let s = a + (b - a) * frac;",
    "      s = Math.max(-1, Math.min(1, s));",
    "      this.buf[this.len++] = s < 0 ? s * 0x8000 : s * 0x7fff;",
    "      if (this.len === this.frame) {",
    "        this.port.postMessage(this.buf.buffer, [this.buf.buffer]);",
    "        this.buf = new Int16Array(this.frame);",
    "        this.len = 0;",
    "      }",
    "      this.pos += this.ratio;",
    "    }",
    "    this.pos -= n;",
    "    this.prev = mono[n - 1];",
    "    return true;",
    "  }",
    "}",
    "registerProcessor('funasr-pcm-capture', PcmCapture);",
  ].join("\n");

  var STATES = ["idle", "connecting", "recording", "stopping", "error"];

  function noop() {}

  // Input level of one frame in 0..1 (RMS, scaled so normal speech reaches ~0.5-1).
  function frameLevel(samples) {
    var sum = 0;
    for (var i = 0; i < samples.length; i++) {
      var s = samples[i] / 32768;
      sum += s * s;
    }
    return Math.min(1, Math.sqrt(sum / (samples.length || 1)) * 4.5);
  }

  function Recognizer(options) {
    options = options || {};
    if (!options.url) throw new Error("FunASR.Recognizer: `url` is required");
    this.url = options.url;
    this.token = options.token || "";
    this.hotwords = options.hotwords || "";
    this.chunkSize = options.chunkSize || [0, 10, 5];
    this.wavName = options.wavName || "microphone";
    this.stopTimeoutMs = options.stopTimeoutMs || 10000;
    this.onPartial = options.onPartial || noop;
    this.onFinal = options.onFinal || noop;
    this.onStateChange = options.onStateChange || noop;
    this.onError = options.onError || noop;
    this.onLevel = options.onLevel || null;

    this.state = "idle";
    this._ws = null;
    this._ctx = null;
    this._stream = null;
    this._node = null;
    this._source = null;
    this._partial = "";
    this._endWaiter = null;
  }

  Recognizer.isSupported = function () {
    return !!(
      typeof window !== "undefined" &&
      window.WebSocket &&
      window.AudioWorkletNode &&
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia
    );
  };

  Recognizer.prototype._setState = function (state) {
    if (this.state === state) return;
    this.state = state;
    this.onStateChange(state);
  };

  Recognizer.prototype._fail = function (err) {
    this._setState("error");
    this.onError(err instanceof Error ? err : new Error(String(err)));
    this._cleanup();
  };

  Recognizer.prototype._buildUrl = function () {
    if (!this.token) return this.url;
    var sep = this.url.indexOf("?") === -1 ? "?" : "&";
    return this.url + sep + "token=" + encodeURIComponent(this.token);
  };

  /** Open the connection only. Use with sendPCM() for non-microphone sources. */
  Recognizer.prototype.connect = function () {
    var self = this;
    if (this._ws) return Promise.resolve();
    this._setState("connecting");
    return new Promise(function (resolve, reject) {
      var ws;
      try {
        ws = new WebSocket(self._buildUrl());
      } catch (e) {
        reject(e);
        return;
      }
      ws.binaryType = "arraybuffer";
      var opened = false;
      ws.onopen = function () {
        opened = true;
        self._ws = ws;
        ws.send(
          JSON.stringify({
            mode: "2pass",
            chunk_size: self.chunkSize,
            wav_name: self.wavName,
            hotwords: self.hotwords,
            is_speaking: true,
          })
        );
        self._setState("recording");
        resolve();
      };
      ws.onmessage = function (ev) {
        self._onMessage(ev.data);
      };
      ws.onerror = function () {
        if (!opened) reject(new Error("WebSocket connection failed: " + self.url));
      };
      ws.onclose = function (ev) {
        var wasOpen = self._ws === ws;
        self._ws = null;
        self._resolveEnd();
        if (!opened) {
          reject(new Error("WebSocket closed before opening (code " + ev.code + ")"));
          return;
        }
        if (ev.code === 4401) self._fail(new Error("Unauthorized: invalid token"));
        else if (ev.code === 4429) self._fail(new Error("Server busy, try again later"));
        else if (wasOpen && self.state !== "idle" && self.state !== "stopping")
          self._fail(new Error("Connection closed (code " + ev.code + ")"));
      };
    });
  };

  Recognizer.prototype._onMessage = function (data) {
    if (typeof data !== "string") return;
    var msg;
    try {
      msg = JSON.parse(data);
    } catch (e) {
      return;
    }
    if (msg.error) {
      this.onError(new Error(msg.error));
      return;
    }
    if (msg.is_end) {
      this._resolveEnd();
      return;
    }
    if (msg.mode === "2pass-online") {
      // Online results are increments; accumulate them for the current sentence.
      this._partial += msg.text || "";
      this.onPartial(this._partial);
    } else if (msg.mode === "2pass-offline") {
      this._partial = "";
      this.onPartial("");
      this.onFinal({
        text: msg.text || "",
        startMs: msg.start_ms,
        endMs: msg.end_ms,
        timestamp: msg.timestamp || [],
      });
    }
  };

  Recognizer.prototype._resolveEnd = function () {
    if (this._endWaiter) {
      var w = this._endWaiter;
      this._endWaiter = null;
      w();
    }
  };

  /** Send raw 16 kHz mono PCM16 samples (Int16Array or ArrayBuffer). */
  Recognizer.prototype.sendPCM = function (pcm) {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return false;
    this._ws.send(pcm instanceof ArrayBuffer ? pcm : pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + pcm.byteLength));
    return true;
  };

  /** Ask for the microphone, connect and start streaming. */
  Recognizer.prototype.start = function () {
    var self = this;
    if (this.state === "recording" || this.state === "connecting") return Promise.resolve();
    if (!Recognizer.isSupported()) {
      var err = new Error(
        "Microphone capture is not available. Use a modern browser over https:// or http://localhost."
      );
      this._fail(err);
      return Promise.reject(err);
    }
    this._partial = "";
    this._setState("connecting");

    var micPromise = navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });

    return micPromise
      .then(function (stream) {
        self._stream = stream;
        var Ctx = window.AudioContext || window.webkitAudioContext;
        self._ctx = new Ctx();
        var blobUrl = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: "application/javascript" }));
        return self._ctx.audioWorklet.addModule(blobUrl).then(function () {
          URL.revokeObjectURL(blobUrl);
        });
      })
      .then(function () {
        return self.connect();
      })
      .then(function () {
        self._source = self._ctx.createMediaStreamSource(self._stream);
        self._node = new AudioWorkletNode(self._ctx, "funasr-pcm-capture", {
          processorOptions: { targetRate: TARGET_RATE, frameSamples: FRAME_SAMPLES },
        });
        self._node.port.onmessage = function (ev) {
          self.sendPCM(ev.data);
          if (self.onLevel) self.onLevel(frameLevel(new Int16Array(ev.data)));
        };
        self._source.connect(self._node);
        // Some browsers only pull audio through nodes that reach the destination.
        // The worklet produces no output, so this is silent.
        self._node.connect(self._ctx.destination);
        if (self._ctx.state === "suspended") return self._ctx.resume();
      })
      .then(function () {
        self._setState("recording");
      })
      .catch(function (e) {
        self._fail(e);
        throw e;
      });
  };

  /** Stop capture, wait for the last corrected sentence, then close. */
  Recognizer.prototype.stop = function () {
    var self = this;
    if (this.state !== "recording" && this.state !== "connecting") {
      this._cleanup();
      return Promise.resolve();
    }
    this._setState("stopping");
    this._stopCapture();
    var ws = this._ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      this._cleanup();
      this._setState("idle");
      return Promise.resolve();
    }
    return new Promise(function (resolve) {
      var timer = setTimeout(function () {
        self._resolveEnd();
      }, self.stopTimeoutMs);
      self._endWaiter = function () {
        clearTimeout(timer);
        self._cleanup();
        self._setState("idle");
        resolve();
      };
      ws.send(JSON.stringify({ is_speaking: false }));
    });
  };

  /** Discard everything immediately: stop capture and close without waiting for results. */
  Recognizer.prototype.abort = function () {
    this._endWaiter = null;
    var wasActive = this.state !== "idle";
    this._setState("stopping"); // so the close below is not reported as an error
    this._cleanup();
    this._partial = "";
    if (wasActive) this._setState("idle");
    else this.state = "idle";
  };

  Recognizer.prototype._stopCapture = function () {
    if (this._node) {
      this._node.port.onmessage = null;
      this._node.disconnect();
      this._node = null;
    }
    if (this._source) {
      this._source.disconnect();
      this._source = null;
    }
    if (this._stream) {
      this._stream.getTracks().forEach(function (t) {
        t.stop();
      });
      this._stream = null;
    }
    if (this._ctx) {
      this._ctx.close();
      this._ctx = null;
    }
  };

  Recognizer.prototype._cleanup = function () {
    this._stopCapture();
    if (this._ws) {
      var ws = this._ws;
      this._ws = null;
      try {
        ws.close(1000);
      } catch (e) {
        /* already closed */
      }
    }
  };

  return { Recognizer: Recognizer, STATES: STATES, version: "1.0.0" };
});
